"use client";

import { useState } from "react";
import { ArrowDown, ArrowUpRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const introWords = ["projects.", "Two", "industries.", "One", "standard."];
const projects = [{
  number: "Case Study: Rapid Coil Cart Replacement",
  name: "Case Study: Rapid Coil Cart Replacement",
  location: "Houston, Texas",
  year: "2024",
  statement: "A complex structural overhaul designed for high-pressure environments. We integrated reinforced steel and precision welding to ensure operational longevity.",
  image: "https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=dcbq70skqzg",
  srcset: "https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=osmnr8hir2",
  sizes: "https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=uz2x954ad5",
  gallery1: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650264/img/refinery_piping_detail.png",
  gallery2: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650264/img/refinery_control_room.webp",
  extended: "The refinery expansion project required meticulous planning to minimize downtime. Our team utilized advanced structural steel fabrication and automated welding techniques to meet the rigorous safety standards of the petrochemical industry."
}, {
  number: "02 / 03",
  name: "Case Study: Rapid Coil Cart Replacement",
  location: "Houston, Texas",
  year: "2024",
  statement: "A complex structural overhaul designed for high-pressure environments. We integrated reinforced steel and precision welding to ensure operational longevity.",
  image: "https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=1o6hubw5huo",
  srcset: "https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=4u4qbu2gyog",
  sizes: "https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=6arhlpbtpwj",
  gallery1: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650264/img/refinery_piping_detail.png",
  gallery2: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650264/img/refinery_control_room.webp",
  extended: "The refinery expansion project required meticulous planning to minimize downtime. Our team utilized advanced structural steel fabrication and automated welding techniques to meet the rigorous safety standards of the petrochemical industry.",
  isDark: true
}];
function IntroBlock() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return null;
}
type Project = (typeof projects)[number];
function ProjectBlock({
  project,
  index
}: {
  project: Project;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 1;
  const isDark = project.isDark;
  const statementWords = project.statement.split(" ");
  return <div ref={ref} data-index={index} className={`relative w-full ${isDark ? "py-32 md:py-40" : "py-24 md:py-32"}`} style={isDark ? {
    backgroundColor: "#DA2F10",
    color: "#FFFFFF"
  } : undefined}>
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        {index === 0 ? <div className="flex flex-col gap-8">
            <h2 className="w-full text-4xl md:text-6xl font-bold font-serif text-[#1B1B1B] leading-tight">
              <span className="text-[#0000FE]">Case Study:</span> Rapid Coil Cart Replacement
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className={`transition-all duration-1000 ease-out h-full flex flex-col ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <div className="flex flex-col h-full">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" className="relative block w-full p-0 h-auto overflow-hidden rounded-lg shadow-md group hover:bg-transparent" aria-label={`View details of ${project.name}`}>
                        
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
                        <div className="absolute bottom-6 right-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/95 backdrop-blur text-[#1B1B1B] text-xs tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 font-default pointer-events-none">
                          View Details
                          <ArrowUpRight className="size-3.5" />
                        </div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl p-0 overflow-hidden bg-[#F6F6F6]">
                      <DialogHeader className="px-8 pt-8 pb-2">
                        <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{
                      color: "#B94A48"
                    }}>
                          {project.number}
                        </p>
                        <DialogTitle className="font-default font-bold text-3xl md:text-4xl text-[#1B1B1B] leading-tight">
                          {project.name}
                        </DialogTitle>
                        <DialogDescription className="flex items-center gap-2 pt-2 text-sm font-default">
                          <MapPin className="size-3.5" />
                          {project.location} — {project.year}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="px-8 pb-8">
                        <Carousel className="w-full mt-4">
                          <CarouselContent>
                            <CarouselItem data-index={0}>
                              <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={`${project.name} primary`} loading="lazy" className="w-full h-[400px] object-cover rounded-lg" />
                            </CarouselItem>
                            <CarouselItem data-index={1}>
                              <img src={project.gallery1} alt={`${project.name} detail 1`} loading="lazy" className="w-full h-[400px] object-cover rounded-lg" />
                            </CarouselItem>
                            <CarouselItem data-index={2}>
                              <img src={project.gallery2} alt={`${project.name} detail 2`} loading="lazy" className="w-full h-[400px] object-cover rounded-lg" />
                            </CarouselItem>
                          </CarouselContent>
                          <CarouselPrevious className="left-2" />
                          <CarouselNext className="right-2" />
                        </Carousel>
                        <p className="mt-6 text-base leading-relaxed text-[#1B1B1B] font-default">
                          {project.extended}
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <img src="https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=fd9pygzgeqa" data-wvc-srcset="https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=u720mv9spe" data-wvc-sizes="https://amusing-monitor.10web.cloud/wp-content/uploads/2026/06/IMG_20260625_112224-2-scaled.jpg?v=ky6b8emmefs" alt="Industrial facility vertical view" loading="lazy" decoding="async" className="mt-8 w-full h-[800px] object-cover rounded-lg shadow-md" />

                  <div className={`mt-8 space-y-4 border ${isDark ? "border-[#0000FE]" : "border-border"} p-6 rounded-lg`} style={{
                backgroundColor: "#FFFFFF"
              }}>
                    <h4 className="font-bold uppercase tracking-widest text-sm" style={{
                  color: "#0000FE"
                }}>Key Outcomes</h4>
                    <ul className="text-sm md:text-base list-disc pl-5 space-y-1">
                      <li>Coil slitting line restored in 10 hours</li>
                      <li>Production resumed for second shift</li>
                      <li>Mechanical, electrical, and controls support delivered through one integrated team</li>
                      <li>Reduced coordination delays and accelerated startup</li>
                      <li>Minimized downtime and production impact</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-start">
                <div className="mt-24">
                  <div className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
                transitionDelay: "200ms"
              }}>
                    

                    <div className={`flex flex-col gap-10 items-stretch font-default text-base leading-relaxed transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
                  transitionDelay: "300ms",
                  color: "#1B1B1B"
                }}>
                        <div className={`flex-1 space-y-4 border ${isDark ? "border-[#0000FE]" : "border-border"} p-6 rounded-lg`} style={{
                    backgroundColor: "#FFFFFF"
                  }}>
                          <h4 className="font-bold uppercase tracking-widest text-sm" style={{
                      color: "#0000FE"
                    }}>The Challenge</h4>
                          <div className="space-y-4 text-sm md:text-base">
                            <p>
                              A coil slitting manufacturer experienced a complete failure of a critical coil cart, bringing production to an immediate stop. Without the cart operating, material could not move through the line and production could not continue.
                            </p>
                            <p>
                              With downtime increasing by the minute, the customer needed more than labor support. They needed a team capable of coordinating the mechanical, electrical, and controls work required to remove the failed equipment, install the replacement, and return the line to service as quickly as possible.
                            </p>
                          </div>
                        </div>

                        
                        
                        <div className={`flex-1 space-y-4 border ${isDark ? "border-[#0000FE]" : "border-border"} p-6 rounded-lg`} style={{
                    backgroundColor: "#FFFFFF"
                  }}>
                          <h4 className="font-bold uppercase tracking-widest text-sm" style={{
                      color: "#0000FE"
                    }}>Our Solution</h4>
                          <div className="space-y-4 text-sm md:text-base">
                            <p>
                              ABCO South, The Automation Group, and FABCO mobilized a coordinated in-house response to support the replacement effort. Electricians, controls engineers, millwrights, and support personnel worked together as a single team to remove the failed coil cart, install the replacement unit, complete electrical integration, update controls as required, and commission the system.
                            </p>
                            <p>
                              Because the work was supported internally across multiple disciplines, activities that would normally require several contractors and separate schedules were completed in parallel. Mechanical installation, electrical work, controls support, and startup efforts were coordinated together to reduce delays and accelerate recovery.
                            </p>
                            <p>
                              This integrated approach allowed the customer to move from equipment failure to production startup in a fraction of the time a traditional replacement effort would typically require.
                            </p>
                          </div>
                        </div>

                        

                        <div className={`flex-1 space-y-4 border ${isDark ? "border-[#0000FE]" : "border-border"} p-6 rounded-lg`} style={{
                    backgroundColor: "#FFFFFF"
                  }}>
                          <h4 className="font-bold uppercase tracking-widest text-sm" style={{
                      color: "#0000FE"
                    }}>The Outcome</h4>
                          <div className="space-y-4 text-sm md:text-base">
                            <p>
                              Within 10 hours of beginning work, the replacement coil cart was installed and operational. The customer resumed production in time for second shift, dramatically reducing downtime and limiting disruption to plant operations.
                            </p>
                            <p>
                              By leveraging ABCO South and FABCO’s combined in‑house capabilities, the customer avoided an extended outage and returned to normal production significantly faster than would have been possible relying on internal resources alone.
                            </p>
                          </div>
                        </div>
                      </div>
                    
                  </div>

                  <div className={`mt-10 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                transitionDelay: "800ms"
              }}>
                    <Dialog>
                      <DialogTrigger asChild>
                        <div />
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl bg-[#F6F6F6]">
                        <DialogHeader>
                          <p className="text-xs tracking-[0.3em] uppercase mb-2" style={{
                        color: "#B94A48"
                      }}>
                            {project.number}
                          </p>
                          <DialogTitle className="font-default font-bold text-3xl text-[#1B1B1B]">
                            {project.name}
                          </DialogTitle>
                          <DialogDescription className="font-default">
                            {project.location} — {project.year}
                          </DialogDescription>
                        </DialogHeader>
                        <p className="text-base leading-relaxed text-[#1B1B1B] font-default">
                          {project.extended}
                        </p>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>
            </div>
          </div> : <div className="flex flex-col gap-8">
            <h2 className="w-full text-4xl md:text-6xl font-bold font-serif text-[#1B1B1B] leading-tight">
              <span className="text-[#FFFFFF]">Case Study:</span> Conveyor Retrofit Supports New Line Installation and Improves Safety
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className={`transition-all duration-1000 ease-out h-full flex flex-col ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <div className="flex flex-col h-full gap-8">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" className="relative block w-full p-0 h-auto overflow-hidden rounded-lg shadow-md group hover:bg-transparent" aria-label={`View details of ${project.name}`}>
                        
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
                        <div className="absolute bottom-6 right-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/95 backdrop-blur text-[#1B1B1B] text-xs tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 font-default pointer-events-none">
                          View Details
                          <ArrowUpRight className="size-3.5" />
                        </div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl p-0 overflow-hidden bg-[#F6F6F6]">
                      <DialogHeader className="px-8 pt-8 pb-2">
                        <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{
                      color: "#B94A48"
                    }}>
                          {project.number}
                        </p>
                        <DialogTitle className="font-default font-bold text-3xl md:text-4xl text-[#1B1B1B] leading-tight">
                          {project.name}
                        </DialogTitle>
                        <DialogDescription className="flex items-center gap-2 pt-2 text-sm font-default">
                          <MapPin className="size-3.5" />
                          {project.location} — {project.year}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="px-8 pb-8">
                        <Carousel className="w-full mt-4">
                          <CarouselContent>
                            <CarouselItem data-index={0}>
                              <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={`${project.name} primary`} loading="lazy" className="w-full h-[400px] object-cover rounded-lg" />
                            </CarouselItem>
                            <CarouselItem data-index={1}>
                              <img src={project.gallery1} alt={`${project.name} detail 1`} loading="lazy" className="w-full h-[400px] object-cover rounded-lg" />
                            </CarouselItem>
                            <CarouselItem data-index={2}>
                              <img src={project.gallery2} alt={`${project.name} detail 2`} loading="lazy" className="w-full h-[400px] object-cover rounded-lg" />
                            </CarouselItem>
                          </CarouselContent>
                          <CarouselPrevious className="left-2" />
                          <CarouselNext className="right-2" />
                        </Carousel>
                        <p className="mt-6 text-base leading-relaxed text-[#1B1B1B] font-default">
                          {project.extended}
                        </p>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <div className="space-y-4 border border-border p-6 rounded-lg" style={{
                backgroundColor: "#FFFFFF",
                color: "#374151"
              }}>
                    <h4 className="font-bold uppercase tracking-widest text-sm" style={{
                  color: "#0000FE"
                }}>Key Outcomes</h4>
                    <ul className="text-sm md:text-base list-disc pl-5 space-y-1">
                      <li>Existing conveyor retrofitted instead of replaced</li>
                      <li>Machine successfully relocated into a smaller footprint</li>
                      <li>Controls and sensors updated to maintain process performance</li>
                      <li>Improved operator access and emergency egress</li>
                      <li>Supported production expansion while meeting safety guidelines</li>
                    </ul>
                  </div>

                  <div className="space-y-4 border border-border p-6 rounded-lg" style={{
                backgroundColor: "#FFFFFF",
                color: "#1B1B1B"
              }}>
                    <h4 className="font-bold uppercase tracking-widest text-sm" style={{
                  color: "#0000FE"
                }}>Our Solution</h4>
                    <div className="space-y-4 text-sm md:text-base">
                      <p>
                        ABCO South Automation Group evaluated the existing conveyor system and completed a retrofit to fit the updated layout requirements. Our team shortened the conveyor and modified the controls and sensor systems to maintain the intended process flow and machine operation.
                      </p>
                      <p>
                        The retrofit was designed to preserve production performance while adapting the equipment to safely operate within the available floor space.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-start">
                <div className="mt-24">
                  <div className={`flex flex-col gap-8 items-stretch font-default text-base leading-relaxed transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
                transitionDelay: "300ms",
                color: "#374151"
              }}>
                    <div className="h-full space-y-4 border border-border p-6 rounded-lg flex-1" style={{
                  backgroundColor: "#FFFFFF"
                }}>
                      <h4 className="font-bold uppercase tracking-widest text-sm" style={{
                    color: "#0000FE"
                  }}>The Challenge</h4>
                      <div className="space-y-4 text-sm md:text-base">
                        <p>
                          A medical device manufacturer was preparing to install new production lines and needed to relocate an existing machine into a smaller area of the facility. During planning, it became clear that the existing finished-part conveyor was too long for the new space and reduced operator access around the machine.
                        </p>
                        <p>
                          In its original configuration, the layout did not provide adequate exit access for operators to move out of the area quickly in the event of an emergency, creating a safety concern and limiting the ability to run the equipment in the new location.
                        </p>
                      </div>
                    </div>

                    <div className="h-full space-y-4 border border-border p-6 rounded-lg flex-1" style={{
                  backgroundColor: "#FFFFFF"
                }}>
                      <h4 className="font-bold uppercase tracking-widest text-sm" style={{
                    color: "#0000FE"
                  }}>The Outcome</h4>
                      <div className="space-y-4 text-sm md:text-base">
                        <p>
                          The customer was able to successfully relocate and operate the machine in its new location while maintaining proper operator access and aligning with facility safety requirements.
                        </p>
                        <p>
                          By modifying and reusing existing equipment instead of replacing the entire conveyor system, the customer reduced disruption, supported their production expansion plans, and created a safer operating environment.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}
function TransitionCue({
  index
}: {
  index: number;
}) {
  return null;
}
export default function IndustrialCaseStudy() {
  return <section data-section-id="4432" id="industrialcasestudy" className="relative bg-[#F6F6F6] text-[#1B1B1B] overflow-hidden">
      <IntroBlock />

      {projects.map((project, i) => <div key={project.number} data-index={i}>
          <ProjectBlock project={project} index={i} />
          {i < projects.length - 1 && !project.isDark && <TransitionCue index={i} />}
        </div>)}
    </section>;
}